import axios from 'axios'

export const api = axios.create({
  baseURL: '',
  timeout: 30000,
  headers: {
    'Content-Type': 'application/json',
  },
})

// Request interceptor to add auth token and ensure correct URL
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('auth_token')
    if (token) {
      config.headers = {
        ...config.headers,
        Authorization: `Bearer ${token}`
      } as any
    }

    // Normalize URL to always go through the /api proxy path from the browser's perspective
    if (config.url) {
      let requestUrl = config.url;

      // If the URL is absolute and targets 'backend:8000', rewrite it to use the proxy path
      if (requestUrl.startsWith('http://backend:8000')) {
        const url = new URL(requestUrl);
        requestUrl = `/api${url.pathname}${url.search}`;
      }
      // If the URL is relative and starts with /v1/, prepend /api
      else if (requestUrl.startsWith('/v1/')) {
        requestUrl = `/api${requestUrl}`;
      }
      // If the URL is relative and starts with /api/v1/, it's already correct for the proxy
      // No change needed for paths like /api/v1/auth/login

      // Ensure the final URL is absolute relative to the current origin
      if (!requestUrl.startsWith('http')) {
        config.url = new URL(requestUrl, window.location.origin).toString();
      } else {
        config.url = requestUrl; // Keep absolute URLs as is if they don't target backend:8000
      }
    }
    return config
  },
  (error) => {
    return Promise.reject(error)
  }
)

// Response interceptor to handle auth errors
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      // Token expired or invalid
      localStorage.removeItem('auth_token')
      
      // Clear any auth headers
      delete api.defaults.headers.common['Authorization']
      
      // Show a brief notification that the session expired
      console.log('Session expired. Please log in again.')
      
      // Redirect to home page where they can re-authenticate
      // But avoid redirect loops
      if (window.location.pathname !== '/' && !window.location.pathname.startsWith('/auth')) {
        window.location.href = '/?session_expired=true'
      }
    }
    return Promise.reject(error)
  }
)

// API endpoints
export const endpoints = {
  auth: {
    login: '/v1/auth/login',
    callback: '/v1/auth/callback',
    refresh: '/v1/auth/refresh',
    logout: '/v1/auth/logout',
    me: '/v1/auth/me',
  },
  folders: {
    list: '/v1/folders/',
    create: '/v1/folders/',
    get: (id: number) => `/v1/folders/${id}`,
    update: (id: number) => `/v1/folders/${id}`,
    delete: (id: number) => `/v1/folders/${id}`,
    albums: (id: number) => `/v1/folders/${id}/albums`,
    uploadImage: '/v1/folders/upload-image',
    reorder: '/v1/folders/reorder',
  },
  albums: {
    list: '/v1/albums/',
    create: '/v1/albums/',
    get: (id: number) => `/v1/albums/${id}`,
    update: (id: number) => `/v1/albums/${id}`,
    delete: (id: number) => `/v1/albums/${id}`,
    addToFolder: (albumId: number, folderId: number) => `/v1/albums/${albumId}/folders/${folderId}`,
    removeFromFolder: (albumId: number, folderId: number) => `/v1/albums/${albumId}/folders/${folderId}`,
    addTag: (albumId: number, tagName: string) => `/v1/albums/${albumId}/tags/${tagName}`,
    removeTag: (albumId: number, tagName: string) => `/v1/albums/${albumId}/tags/${tagName}`,
    spotifySearch: '/v1/albums/spotify/search',
    spotifyDetails: (spotifyId: string) => `/v1/albums/spotify/${spotifyId}`,
  },
  search: {
    search: '/v1/search/',
    suggestions: '/v1/search/suggestions',
    recent: '/v1/search/recent',
    popular: '/v1/search/popular',
  },
  tags: {
    list: '/v1/tags/',
    create: '/v1/tags/',
    get: (name: string) => `/v1/tags/${name}`,
    delete: (name: string) => `/v1/tags/${name}`,
    albums: (name: string) => `/v1/tags/${name}/albums`,
    trending: '/v1/tags/popular/trending',
  },
  genres: {
    list: '/v1/genres/',
    create: '/v1/genres/',
    get: (name: string) => `/v1/genres/${name}`,
    albums: (name: string) => `/v1/genres/${name}/albums`,
  },
  artists: {
    list: '/v1/artists/',
    get: (name: string) => `/v1/artists/${name}`,
    albums: (name: string) => `/v1/artists/${name}/albums`,
  },
  stats: {
    get: '/v1/stats/',
  },
  maintenance: {
    status: '/v1/maintenance/status',
    reload: '/v1/maintenance/reload-albums',
    clearCache: '/v1/maintenance/clear-cache',
    stats: '/v1/maintenance/stats',
    tokens: '/v1/maintenance/tokens',
    revokeToken: (id: number) => `/v1/maintenance/tokens/${id}`,
  },
}
